import React, { useState, useEffect } from "react";
import "./SubmitReport.css";
import TransLoader from "../../loader/TransLoader";
import CustomAlert from "../../custom_alert/CustomAlert";

export default function SubmitReport() {
  const [config, setConfig] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [selectedTaskId, setSelectedTaskId] = useState("");
  const [taskUpdates, setTaskUpdates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [alertMessage, setAlertMessage] = useState("");

  // Modal states
  const [showImagesModal, setShowImagesModal] = useState(false);
  const [selectedImages, setSelectedImages] = useState([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [updateDescription, setUpdateDescription] = useState("");
  const [updateImages, setUpdateImages] = useState([]);

  const empCode = sessionStorage.getItem("user_id");

  // Load config.json
  useEffect(() => {
    fetch("/config.json")
      .then((res) => res.json())
      .then((data) => setConfig(data))
      .catch(console.error);
  }, []);

  // Fetch tasks for dropdown
  useEffect(() => {
    if (!config || !empCode) return;

    const fetchTasks = async () => {
      setLoading(true);
      try {
        const res = await fetch(`${config.BASE_URL}${config.EMPLOYEE_TASKS}`, {
          method: "POST",
          credentials: "include",
          headers: { "X-Requested-With": "RC_EMP_NEW" },
          body: JSON.stringify({
            emp_code: empCode
          })
        });
        const data = await res.json();
        if (data.status === "success") {
          const myTasks = (data.pending || []).concat(data.completed || []);
          setTasks(myTasks);
        } else {
          setAlertMessage(data.message || "Failed to fetch tasks.");
        }
      } catch (err) {
        console.error(err);
        setAlertMessage("Network error while fetching tasks.");
      } finally {
        setLoading(false);
      }
    };

    fetchTasks();
  }, [config, empCode]);

  // Fetch updates for selected task
  const fetchUpdates = async (taskId) => {
    if (!config || !taskId || !empCode) return;
    setLoading(true);
    try {
      const res = await fetch(`${config.BASE_URL}${config.GET_TASK_UPDATES}`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json", "X-Requested-With": "RC_EMP_NEW" },
        body: JSON.stringify({ task_id: taskId, emp_code: empCode }),
      });
      const data = await res.json();
      if (data.status === "success") {
        const updates = data.updates.map((u) => {
          const images = [];
          for (let i = 1; i <= 5; i++) {
            if (u[`image${i}`]) images.push(u[`image${i}`]);
          }
          return { ...u, images };
        });
        setTaskUpdates(updates);
      } else {
        setAlertMessage(data.message || "Failed to fetch updates.");
      }
    } catch (err) {
      console.error(err);
      setAlertMessage("Network error while fetching updates.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedTaskId) fetchUpdates(selectedTaskId);
    else setTaskUpdates([]);
  }, [selectedTaskId]);

  // Modal: view images
  const handleShowImages = (images) => {
    if (!images || images.length === 0) {
      setAlertMessage("No images available for this update.");
      return;
    }
    setSelectedImages(images);
    setCurrentImageIndex(0);
    setShowImagesModal(true);
  };

  const closeImagesModal = () => {
    setShowImagesModal(false);
    setSelectedImages([]);
    setCurrentImageIndex(0);
  };

  const nextImage = () => setCurrentImageIndex((prev) => (prev + 1) % selectedImages.length);
  const prevImage = () => setCurrentImageIndex((prev) => (prev - 1 + selectedImages.length) % selectedImages.length);

  // Handle new update submission
  const handleSubmitUpdate = async () => {
    if (!updateDescription.trim()) {
      setAlertMessage("Description is mandatory.");
      return;
    }

    const formData = new FormData();
    formData.append("task_id", selectedTaskId);
    formData.append("emp_code", empCode);
    formData.append("remark", updateDescription.trim());
    formData.append("date", new Date().toISOString().slice(0, 10));

    // Append images (up to 5)
    updateImages.slice(0, 5).forEach((file, idx) => formData.append(`image${idx + 1}`, file));

    try {
      const res = await fetch(`${config.BASE_URL}${config.SUBMIT_TASK_UPDATE}`, {
        method: "POST",
        credentials: "include",
        headers: { "X-Requested-With": "RC_EMP_NEW" },
        body: formData,
      });
      const data = await res.json();
      if (data.status === "success") {
        setAlertMessage("Update submitted successfully!");
        setShowUpdateModal(false);
        setUpdateDescription("");
        setUpdateImages([]);
        fetchUpdates(selectedTaskId);
      } else {
        setAlertMessage(data.message || "Update submission failed.");
      }
    } catch (err) {
      console.error(err);
      setAlertMessage("Network error while submitting update.");
    }
  };

  const selectedTask = tasks.find(t => t.task_id === selectedTaskId);

  if (loading) return <TransLoader />;

  return (
    <>
      <div className="content-card">
        <h3>Submit Task Update</h3>

        {/* Task selection */}
        <div className="task-dropdown">
          <label>Select Task:</label>
          <select value={selectedTaskId} onChange={(e) => setSelectedTaskId(e.target.value)}>
            <option value="">-- Select Task --</option>
            {tasks.map((t) => (
              <option key={t.task_id} value={t.task_id}>
                {t.job_title} - {t.job_description}
              </option>
            ))}
          </select>
        </div>

        {/* Selected Task Details */}
        {selectedTask && (
          <div className="task-details">
            <h4 className="task-title">{selectedTask.job_title}</h4>
            <p className="task-description">{selectedTask.job_description}</p>
          </div>
        )}

        {/* Previous updates */}
        {selectedTaskId && (
          <div className="updates-section">
            <h4>Previous Updates</h4>
            <table className="updates-table">
              <thead>
                <tr>
                  <th>S.No</th>
                  <th>Date</th>
                  <th>Remark</th>
                  <th>Images</th>
                </tr>
              </thead>
              <tbody>
                {taskUpdates.length > 0 ? (
                  taskUpdates.map((u, idx) => (
                    <tr key={u.update_id || idx}>
                      <td>{idx + 1}</td>
                      <td>{u.update_date}</td>
                      <td>{u.remark}</td>
                      <td>
                        {u.images.length > 0 ? (
                          <button onClick={() => handleShowImages(u.images)}>View Images</button>
                        ) : (
                          "No Images"
                        )}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="4">No updates available</td>
                  </tr>
                )}
              </tbody>
            </table>

            <button className="add-update-btn" onClick={() => setShowUpdateModal(true)}>
              Add Update
            </button>
          </div>
        )}
      </div>

      {/* Modal: add update */}
      {showUpdateModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <span className="close-btn" onClick={() => setShowUpdateModal(false)}>&times;</span>
            <h4>Submit Update</h4>
            <textarea
              placeholder="Enter description (mandatory)"
              value={updateDescription}
              onChange={(e) => setUpdateDescription(e.target.value)}
            />
            <input
              type="file"
              multiple
              onChange={(e) => setUpdateImages([...e.target.files])}
            />
            <button onClick={handleSubmitUpdate}>Submit</button>
          </div>
        </div>
      )}

      {/* Modal: view images */}
      {showImagesModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <span className="close-btn" onClick={closeImagesModal}>&times;</span>
            <div className="image-container">
              <button onClick={prevImage}>&larr;</button>
              <img
                key={currentImageIndex}
                src={`data:image/jpeg;base64,${selectedImages[currentImageIndex]}`}
                alt={`Update Image ${currentImageIndex + 1}`}
              />
              <button onClick={nextImage}>&rarr;</button>
            </div>
            <p>
              Image {currentImageIndex + 1} of {selectedImages.length}
            </p>
          </div>
        </div>
      )}

      <CustomAlert message={alertMessage} onClose={() => setAlertMessage("")} />
    </>
  );
}
